/**
 * The animation clock (after the dungeon-floors prototype's
 * `dungeon-motion.js`). Rendering is on demand (ADR 0005): frames are drawn
 * only while a tween runs, and the loop stops when the last one ends. At speed
 * 0 every tween jumps to its end at once (tests replay whole runs that way).
 */
import { presentation } from './constants.ts';

/** `since`: when the tween last stepped (its start at first), so a tween started between two frames counts from its own start. */
type Tween = { readonly duration: number; elapsed: number; since: number; readonly step: (t: number) => void; readonly resolve: () => void };

export class Animator {
  private readonly tweens: Tween[] = [];
  private speed = 1;
  private frame: number | null = null;
  private last = 0;
  private disposed = false;
  /** Whether the last tick scheduled the next one: only then is the next interval a frame-to-frame interval. */
  private streak = false;
  private readonly intervals: number[] = [];

  constructor(
    private readonly onFrame: () => void,
    private readonly keep: number,
  ) {}

  get busy(): boolean {
    return this.tweens.length > 0;
  }

  /**
   * Intervals between back-to-back animation frames, ms, oldest first (the
   * last `keep`): the pacing of an animation as it plays. Never an idle gap:
   * the first frame of an animation has no interval, and `breakStreak` (a
   * hidden page, a tier change) drops the one in progress.
   */
  animationIntervals(): readonly number[] {
    return [...this.intervals];
  }

  breakStreak(): void {
    this.streak = false;
  }

  /** 1 is normal speed, 2 twice as fast; 0 makes every animation instant. */
  setSpeed(speed: number): void {
    if (!(speed >= 0)) throw new Error(`animation speed must be 0 or more, got ${speed}`);
    this.speed = speed;
    if (speed === 0) this.finishAll();
  }

  /** Runs `step(t)` for t from 0 to 1 over `durationMs`; resolves after `step(1)`. */
  run(durationMs: number, step: (t: number) => void): Promise<void> {
    // After dispose nothing runs and no frame is scheduled; the caller's loop sees `disposed` and stops.
    if (this.disposed) return Promise.resolve();
    if (this.speed === 0 || durationMs <= 0) {
      step(1);
      return Promise.resolve();
    }
    return new Promise((resolve) => {
      step(0);
      const now = performance.now();
      this.tweens.push({ duration: durationMs / this.speed, elapsed: 0, since: now, step, resolve });
      if (this.frame === null) {
        this.last = now;
        this.streak = false;
        this.frame = requestAnimationFrame(this.tick);
      }
    });
  }

  get isDisposed(): boolean {
    return this.disposed;
  }

  /** Stops the clock for good: pending tweens resolve without another step, and no frame is scheduled again. */
  dispose(): void {
    this.disposed = true;
    if (this.frame !== null) cancelAnimationFrame(this.frame);
    this.frame = null;
    for (const t of this.tweens.splice(0)) t.resolve();
  }

  private finishAll(): void {
    for (const t of this.tweens.splice(0)) {
      t.step(1);
      t.resolve();
    }
  }

  private readonly tick = (time: number): void => {
    if (this.disposed) return;
    if (this.streak) {
      this.intervals.push(time - this.last);
      if (this.intervals.length > this.keep) this.intervals.shift();
    }
    this.last = time;
    for (const t of [...this.tweens]) {
      t.elapsed = Math.min(t.duration, t.elapsed + Math.min(Math.max(0, time - t.since), presentation.anim.maxFrameMs));
      t.since = Math.max(t.since, time);
      t.step(t.elapsed / t.duration);
      if (t.elapsed >= t.duration) {
        this.tweens.splice(this.tweens.indexOf(t), 1);
        t.resolve();
      }
    }
    this.onFrame();
    this.frame = this.tweens.length > 0 && !this.disposed ? requestAnimationFrame(this.tick) : null;
    this.streak = this.frame !== null;
  };
}
